import { useEffect } from 'react';
import { dehydrate, type DehydratedState, QueryClient } from '@tanstack/react-query';
import { getLocaleCodeByCountryCode, SUPPORTED_LOCALES_OBJ, type SupportedLocale } from '@zep-us/i18n';
import { type GetServerSidePropsContext, type GetServerSidePropsResult, type NextPageWithLayout } from 'next';
import { useRouter } from 'next/router';
import { getGetQuizUserInfoQueryKey, getQuizUserInfo, type searchUserPublicQuizsets } from '@/api';
import { type QueryDataType } from '@/api/api.type';
import { getRootLayout } from '@/layout';
import { getDefaultQuizsetSearchReq, useFetchUserQuizes } from '@/module/profile/user/hooks/useFetchUserQuizes';
import { getUserProfileSeoContent } from '@/module/profile/user/seo';
import { UserProfilePage } from '@/module/profile/user/UserProfilePage';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { getDefaultServerSideProps } from '@/utils';
import { setPublicPageCache } from '@/utils/ssr';
type UserProfilePageProps = {
  userHashId: string;
  seoContent: object;
  canonicalUrl: string;
  dehydratedState: DehydratedState;
};
const UserProfilePageComponent: NextPageWithLayout<UserProfilePageProps> = ({
  userHashId
}) => {
  const isInExternalPlatform = useIsInExternalPlatform();
  const router = useRouter();
  useEffect(() => {
    if (isInExternalPlatform) {
      router.back();
    }
  }, [isInExternalPlatform, router]);
  if (isInExternalPlatform) return null;
  return <UserProfilePage userHashId={userHashId} data-sentry-element="UserProfilePage" data-sentry-component="UserProfilePageComponent" data-sentry-source-file="index.page.tsx" />;
};
UserProfilePageComponent.getLayout = getRootLayout;
export const getServerSideProps = async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<UserProfilePageProps>> => {
  const {
    params
  } = context;
  const queryClient = new QueryClient();
  const userHashId = params?.userHashId as string ?? null;
  if (!userHashId) {
    return {
      notFound: true
    };
  }
  // 프로필 페이지는 항상 공개 → Edge 캐싱 활성화
  setPublicPageCache(context, {
    cacheable: true,
    profile: 'user'
  });
  const prefetchUser = queryClient.prefetchQuery({
    queryKey: getGetQuizUserInfoQueryKey(userHashId),
    queryFn: () => getQuizUserInfo(userHashId)
  });
  const quizVariables = {
    userId: userHashId,
    quizsetSearchReq: getDefaultQuizsetSearchReq({
      searchText: context.query['searchText'] ? String(context.query['searchText']) : '',
      order: String(context.query['order'])
    })
  } as const;
  const prefetchUserQuizSet = queryClient.prefetchInfiniteQuery(useFetchUserQuizes.getFetchOptions(quizVariables));
  try {
    const [{
      props: defaultProps
    }] = await Promise.all([getDefaultServerSideProps({
      ...context,
      namespaces: ['common']
    }), prefetchUser, prefetchUserQuizSet]);
    const userRes = queryClient.getQueryData<QueryDataType<typeof getQuizUserInfo>>(getGetQuizUserInfoQueryKey(userHashId));
    const quizsetsRes = queryClient.getQueryData<{
      pages: QueryDataType<typeof searchUserPublicQuizsets>[];
    }>(useFetchUserQuizes.getKey(quizVariables));
    if (!userRes || !userRes.data || !userRes.data.countryCode || !userRes.data.userType) {
      return {
        notFound: true
      };
    }
    // TODO: QuizUserInfoRes 에 updatedAt 이 노출되면 handleIfModifiedSince 적용

    const quizSets = (quizsetsRes?.pages ?? []).length > 0 ? (quizsetsRes?.pages ?? []).flatMap(page => page.data ?? []) : [];
    const mappedLocale = getLocaleCodeByCountryCode(userRes.data.countryCode);
    const canonicalUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${mappedLocale === SUPPORTED_LOCALES_OBJ.ko ? '' : `/${mappedLocale}`}/profile/${userHashId}`;
    const seoContent = getUserProfileSeoContent({
      user: {
        ...userRes.data,
        userHashId
      },
      quizSets: quizSets,
      _nextI18Next: defaultProps._nextI18Next,
      locale: context.locale as SupportedLocale,
      canonicalUrl
    });
    return {
      props: {
        seoContent,
        canonicalUrl,
        userHashId,
        ...defaultProps,
        dehydratedState: dehydrate(queryClient)
      }
    };
  } catch (e) {
    console.error(e);
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default UserProfilePageComponent;